/**
 * Payroll page — period view of all employee payments.
 *
 * Composition:
 *   - PageHeader + "Generate payroll" + "Bulk approve & pay" actions
 *   - KPI strip (Total period payroll, Pending, Avg salary, Departments)
 *   - DataTable: employee, period, base, OT, bonus, deductions, net (finance.read gated), status
 *   - Period selector (last 3 months) for the row filter
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Banknote, CheckCheck, Filter, Sparkles } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneyCompact } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  StatusPill,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { EmployeeCard } from "../../../components/workforce/EmployeeCard";
import { useHasPermission } from "../../../lib/permissions";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockEmployeePayments,
  mockEmployees,
  type MockEmployee,
  type MockEmployeePayment,
  type MockPaymentStatus,
} from "../../../lib/workforceMock";

export const Route = createFileRoute("/_app/workforce/payroll")({
  component: PayrollPage,
});

const MONTH = 30 * 24 * 60 * 60 * 1000;

const STATUS_VARIANT: Record<
  MockPaymentStatus,
  "info" | "success" | "danger" | "warning" | "neutral"
> = {
  draft: "info",
  paid: "success",
  void: "neutral",
};

type PeriodOption = {
  key: string;
  label: string;
  from: number;
  to: number;
};

function buildPeriods(): PeriodOption[] {
  const now = new Date();
  const out: PeriodOption[] = [];
  for (let i = 0; i < 3; i++) {
    const monthStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1);
    const monthEnd = Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i + 1, 1) - 1;
    out.push({
      key: `${monthStart}`,
      label: new Date(monthStart).toLocaleDateString("en-BD", {
        month: "long",
        year: "numeric",
      }),
      from: monthStart,
      to: monthEnd,
    });
  }
  return out;
}

function PayrollPage() {
  const periods = useMemo(buildPeriods, []);
  const [periodKey, setPeriodKey] = useState<string>(periods[0]!.key);

  const [rows, setRows] = usePersistentMockState<MockEmployeePayment[]>(
    "employeePayments",
    mockEmployeePayments,
  );
  const [allEmployees] = usePersistentMockState<MockEmployee[]>(
    "employees",
    mockEmployees,
  );
  const canSeeFinance = useHasPermission("finance.read");
  const canApprove = useHasPermission("payroll.approve");

  const period = periods.find((p) => p.key === periodKey) ?? periods[0]!;

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) => r.periodFrom >= period.from && r.periodFrom <= period.to,
      ),
    [rows, period],
  );

  const findEmployeeLocal = useMemo(
    () => (id: string) => allEmployees.find((e) => e._id === id),
    [allEmployees],
  );

  // KPIs
  const totalPeriod = useMemo(
    () =>
      filtered
        .filter((r) => r.status !== "void")
        .reduce((s, r) => s + r.netInBaseCents, 0n),
    [filtered],
  );
  const pendingCount = useMemo(
    () => filtered.filter((r) => r.status === "draft").length,
    [filtered],
  );
  const pendingValue = useMemo(
    () =>
      filtered
        .filter((r) => r.status === "draft")
        .reduce((s, r) => s + r.netInBaseCents, 0n),
    [filtered],
  );
  const activeEmps = useMemo(
    () => allEmployees.filter((e) => e.isActive),
    [allEmployees],
  );
  const avgSalary = useMemo(
    () =>
      activeEmps.length === 0
        ? 0n
        : activeEmps.reduce((s, e) => s + e.baseSalaryCents, 0n) /
          BigInt(activeEmps.length),
    [activeEmps],
  );

  const topDept = useMemo(() => {
    const byDept = new Map<string, bigint>();
    for (const e of activeEmps) {
      byDept.set(
        e.department,
        (byDept.get(e.department) ?? 0n) + e.baseSalaryCents,
      );
    }
    return Array.from(byDept.entries()).sort((a, b) =>
      a[1] > b[1] ? -1 : 1,
    )[0];
  }, [activeEmps]);

  function generatePayroll() {
    if (!canApprove) return;
    const now = Date.now();
    const newDrafts: MockEmployeePayment[] = [];
    for (const emp of activeEmps) {
      const existing = filtered.find((r) => r.employeeId === emp._id);
      if (existing) continue;
      newDrafts.push({
        _id: `pay_gen_${emp._id}_${now}`,
        employeeId: emp._id,
        paymentType: "salary",
        periodFrom: period.from,
        periodTo: period.to,
        baseSalaryCents: emp.baseSalaryCents,
        overtimeCents: 0n,
        bonusCents: 0n,
        deductionsCents: 0n,
        netCents: emp.baseSalaryCents,
        currency: emp.salaryCurrency,
        fxRateToBaseE8: 100_000_000n,
        netInBaseCents: emp.baseSalaryCents,
        paidAt: null,
        paymentMethod: "bank_transfer",
        referenceNumber: null,
        status: "draft",
        notes: "Auto-generated payroll draft.",
      });
    }
    if (newDrafts.length === 0) {
      toast.info("Already generated for this period");
      return;
    }
    setRows((prev) => [...newDrafts, ...prev]);
    toast.success(`Generated ${newDrafts.length} draft payment(s)`);
  }

  function bulkApprove() {
    if (!canApprove) return;
    const drafts = filtered.filter((r) => r.status === "draft");
    if (drafts.length === 0) {
      toast.info("No drafts to approve");
      return;
    }
    setRows((prev) =>
      prev.map((r) =>
        drafts.some((d) => d._id === r._id)
          ? { ...r, status: "paid", paidAt: Date.now() }
          : r,
      ),
    );
    toast.success(`Marked ${drafts.length} payment(s) as paid`);
  }

  const columns = useMemo<ColumnDef<MockEmployeePayment>[]>(
    () => [
      {
        id: "employee",
        header: "Employee",
        cell: ({ row }) => {
          const emp = findEmployeeLocal(row.original.employeeId);
          if (!emp) {
            return <span className="text-(--text-tertiary)">Unknown</span>;
          }
          return <EmployeeCard employee={emp} size="sm" linkToProfile />;
        },
      },
      {
        id: "period",
        header: "Period",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-secondary)">
            {formatDate(row.original.periodFrom, { style: "short" })} →{" "}
            {formatDate(row.original.periodTo, { style: "short" })}
          </span>
        ),
      },
      {
        id: "base",
        header: "Base",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums">
              {formatMoney(row.original.baseSalaryCents, row.original.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "ot",
        header: "OT",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums text-(--text-secondary)">
              {formatMoney(row.original.overtimeCents, row.original.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "bonus",
        header: "Bonus",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums text-(--text-secondary)">
              {formatMoney(row.original.bonusCents, row.original.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "deductions",
        header: "Deductions",
        meta: { numeric: true, mono: true, hideOnMobile: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums text-(--text-secondary)">
              {formatMoney(
                row.original.deductionsCents,
                row.original.currency,
                { locale: "en-BD" },
              )}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        id: "net",
        header: "Net",
        meta: { numeric: true, mono: true },
        cell: ({ row }) =>
          canSeeFinance ? (
            <span className="font-mono tabular-nums font-medium text-(--text-primary)">
              {formatMoney(row.original.netCents, row.original.currency, {
                locale: "en-BD",
              })}
            </span>
          ) : (
            <span className="text-(--text-tertiary)">—</span>
          ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill
            variant={STATUS_VARIANT[row.original.status]}
            size="sm"
          >
            {row.original.status}
          </StatusPill>
        ),
      },
    ],
    [canSeeFinance, findEmployeeLocal],
  );

  function handleExportCsv(extension: "csv" | "xls") {
    type PayRow = {
      employeeCode: string;
      employeeName: string;
      department: string;
      paymentType: string;
      periodFrom: string;
      periodTo: string;
      baseSalaryCents: bigint;
      overtimeCents: bigint;
      bonusCents: bigint;
      deductionsCents: bigint;
      netCents: bigint;
      currency: string;
      netInBaseCents: bigint;
      paymentMethod: string;
      referenceNumber: string;
      status: string;
      paidAt: string;
    };
    const rows: PayRow[] = filtered.map((p) => {
      const emp = findEmployeeLocal(p.employeeId);
      return {
        employeeCode: emp?.code ?? "",
        employeeName: emp?.fullName ?? "",
        department: emp?.department ?? "",
        paymentType: p.paymentType,
        periodFrom: formatDateForCsv(p.periodFrom),
        periodTo: formatDateForCsv(p.periodTo),
        baseSalaryCents: canSeeFinance ? p.baseSalaryCents : 0n,
        overtimeCents: canSeeFinance ? p.overtimeCents : 0n,
        bonusCents: canSeeFinance ? p.bonusCents : 0n,
        deductionsCents: canSeeFinance ? p.deductionsCents : 0n,
        netCents: canSeeFinance ? p.netCents : 0n,
        currency: p.currency,
        netInBaseCents: canSeeFinance ? p.netInBaseCents : 0n,
        paymentMethod: p.paymentMethod,
        referenceNumber: p.referenceNumber ?? "",
        status: p.status,
        paidAt: formatDateForCsv(p.paidAt),
      };
    });
    const columns: ExportColumn<PayRow>[] = [
      { key: "employeeCode", label: "Employee code" },
      { key: "employeeName", label: "Employee" },
      { key: "department", label: "Department" },
      { key: "paymentType", label: "Type" },
      { key: "periodFrom", label: "Period from" },
      { key: "periodTo", label: "Period to" },
      ...(canSeeFinance
        ? ([
            { key: "baseSalaryCents", label: "Base" },
            { key: "overtimeCents", label: "OT" },
            { key: "bonusCents", label: "Bonus" },
            { key: "deductionsCents", label: "Deductions" },
            { key: "netCents", label: "Net" },
            { key: "currency", label: "Currency" },
            { key: "netInBaseCents", label: "Net BDT" },
          ] as ExportColumn<PayRow>[])
        : []),
      { key: "paymentMethod", label: "Method" },
      { key: "referenceNumber", label: "Reference" },
      { key: "status", label: "Status" },
      { key: "paidAt", label: "Paid at" },
    ];
    downloadCsv(
      exportFilename(`payroll-${period.label.toLowerCase().replace(/\s+/g, "-")}`, extension),
      toCsv(rows, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Payroll"
        description="Generate, approve, and track employee payments per pay period."
        actions={
          <div className="flex items-center gap-2">
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            {canApprove ? (
              <>
                <Button variant="outline" onClick={generatePayroll}>
                  <Sparkles className="h-3.5 w-3.5" /> Generate for {period.label}
                </Button>
                <Button onClick={bulkApprove}>
                  <CheckCheck className="h-3.5 w-3.5" /> Bulk approve & pay
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
        aria-label="Payroll KPIs"
      >
        <KpiCard
          caption="Period payroll"
          value={
            canSeeFinance
              ? formatMoneyCompact(totalPeriod, "BDT", { locale: "en-BD" })
              : "Restricted"
          }
          hint={canSeeFinance ? "Net (excl. void)" : "finance.read needed"}
        />
        <KpiCard
          caption="Pending"
          value={pendingCount}
          hint={
            canSeeFinance
              ? formatMoneyCompact(pendingValue, "BDT", { locale: "en-BD" })
              : "Drafts to approve"
          }
        />
        <KpiCard
          caption="Avg salary"
          value={
            canSeeFinance
              ? formatMoneyCompact(avgSalary, "BDT", { locale: "en-BD" })
              : "Restricted"
          }
          hint="Across active employees"
        />
        <KpiCard
          caption="Top department"
          value={topDept ? topDept[0] : "—"}
          hint={
            canSeeFinance && topDept
              ? formatMoneyCompact(topDept[1], "BDT", { locale: "en-BD" })
              : ""
          }
        />
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-3.5 w-3.5 text-(--text-tertiary)" aria-hidden />
        {periods.map((p) => (
          <FilterChip
            key={p.key}
            active={periodKey === p.key}
            onClick={() => setPeriodKey(p.key)}
          >
            {p.label}
          </FilterChip>
        ))}
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        emptyTitle="No payments for this period"
        emptyDescription="Click 'Generate' to draft salary payments for every active employee."
        emptyIcon={<Banknote className="h-5 w-5" aria-hidden />}
      />
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "h-7 rounded-full border px-2.5 text-[11px] transition-colors",
        active
          ? "bg-(--accent) text-(--accent-fg) border-(--accent)"
          : "bg-(--bg-canvas) text-(--text-secondary) border-(--border-subtle) hover:bg-(--bg-hover)",
      )}
    >
      {children}
    </button>
  );
}
